import { createContext, useContext, useMemo, useState } from "react";

import { Attachment } from "@/components/ui/attachment-new";
import { Button } from "@/components/ui/button/button";
import { BwAttachLine } from "@/components/ui/icons";
import { LightBox } from "@/components/ui/lightbox";
import { Upload } from "@/features/shared/upload/invisible-upload";
import type { AttachmentUploadHandle } from "@/libs/attachment-upload-handle";
import {
  areAttachmentsPending,
  type FormAttachment,
} from "@/libs/form-attachment";

type AttachmentUploaderContextType = {
  uploadRef: React.RefObject<AttachmentUploadHandle | null>;
  value: FormAttachment[];
  onChange: (value: FormAttachment[]) => void;
};

const AttachmentUploaderContext =
  createContext<AttachmentUploaderContextType | null>(null);

type AttachmentUploaderProps = {
  value: FormAttachment[];
  onChange: (value: FormAttachment[]) => void;
  children: React.ReactNode;
  ref: React.RefObject<AttachmentUploadHandle | null>;
};

export function AttachmentUploader({
  value,
  onChange,
  children,
  ref,
}: AttachmentUploaderProps) {
  return (
    <AttachmentUploaderContext.Provider
      value={{ uploadRef: ref, value, onChange }}
    >
      {children}
    </AttachmentUploaderContext.Provider>
  );
}

function useAttachmentUploader() {
  const context = useContext(AttachmentUploaderContext);
  if (!context) {
    throw new Error(
      "AttachmentUploader components must be used within AttachmentUploader",
    );
  }
  return context;
}

export function AttachmentUploadAction({
  disabled,
  children,
}: {
  disabled?: boolean;
  children?: React.ReactNode;
}) {
  const { uploadRef, value, onChange } = useAttachmentUploader();

  return (
    <Upload
      ref={uploadRef}
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      {children ?? (
        <Button
          kind="ghost"
          variant="neutral"
          size="icon-sm"
          disabled={disabled}
        >
          <BwAttachLine />
        </Button>
      )}
    </Upload>
  );
}

export function AttachmentUploadView({
  tileClassName = "w-24 shrink-0",
}: {
  /** Each tile's width — a strip has no column for the tile to fill. */
  tileClassName?: string;
}) {
  const { uploadRef, value } = useAttachmentUploader();
  const attachments = value || [];
  const [attachmentIndex, setAttachmentIndex] = useState<number | undefined>(
    undefined,
  );
  const attachmentsFiltered = useMemo(() => {
    return attachments
      .filter((attachment) => Number(attachment.fileSize) > 0)
      .map((attachment) => {
        return {
          ...attachment,
          originalContentUri:
            attachment.originalContentUri || attachment.contentUri || "",
          contentUri:
            attachment.originalContentUri || attachment.contentUri || "",
        };
      });
  }, [attachments]);

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 overflow-x-auto flex-nowrap shrink-0">
      {attachments.map((attachment, index) => (
        <Attachment
          key={`${attachment.id}-${attachment.fileName}`}
          fileName={attachment.fileName || ""}
          originalContentUri={attachment.originalContentUri}
          thumbnailUri={attachment.thumbnailUri}
          fileLayout="details"
          fileSize={attachment.fileSize}
          status={attachment.status}
          percent={attachment.percent}
          className={tileClassName}
          onClick={() => {
            if (areAttachmentsPending([attachment])) return;
            setAttachmentIndex(index);
          }}
          onRemove={() => {
            if (attachment.id) {
              return uploadRef.current?.removeAttachment(attachment.id);
            }
            if (attachment.uid) {
              return uploadRef.current?.removeAttachmentByUid(attachment.uid);
            }
          }}
        />
      ))}
      {attachmentsFiltered?.length > 0 && (
        <LightBox
          open={attachmentIndex !== undefined}
          slideIndex={attachmentIndex}
          attachments={attachmentsFiltered}
          onClose={() => setAttachmentIndex(undefined)}
        />
      )}
    </div>
  );
}
